<!-- src\components\course\CertificateCard.vue -->
<template>
	<div class="certificate-card" :class="{ 'certificate-card--minimal': minimal }">
		<div
			class="certificate-card__preview"
			:class="{ 'certificate-card__preview--image': previewImage }"
		>
			<img
				v-if="previewImage"
				:src="previewImage"
				:alt="certificate.template_name"
				class="certificate-card__preview-image"
			/>
			<div v-else class="certificate-card__mini-preview" :style="miniPreviewStyle">
				<!-- Рамка -->
				<div v-if="templateConfig.borderStyle?.enabled" class="mini-preview__border" :style="borderStyle"></div>
				<!-- Контент -->
				<div class="mini-preview__content">
					<div class="mini-preview__title">СЕРТИФИКАТ</div>
					<div class="mini-preview__subtitle">о прохождении курса</div>
					<div class="mini-preview__variable">Имя студента</div>
					<div class="mini-preview__text">успешно завершил(а) курс</div>
					<div class="mini-preview__course">Название курса</div>
					<div class="mini-preview__date">Дата выдачи</div>
				</div>
			</div>
		</div>

		<div v-if="!minimal" class="certificate-card__content">
			<h4 class="certificate-card__title">{{ certificate.template_name }}</h4>

			<div v-if="certificate.criteria" class="certificate-card__criteria">
				<p class="certificate-card__criteria-label">Критерии получения:</p>
				<ul class="certificate-card__criteria-list">
					<li
						v-for="(criterion, index) in parsedCriteria"
						:key="index"
						class="certificate-card__criteria-item"
					>
						{{ criterion }}
					</li>
				</ul>
			</div>

			<div class="certificate-card__meta">
				<span class="certificate-card__meta-item">
					📊 Выдано: {{ certificate.issued_count || 0 }}
				</span>
			</div>
		</div>

		<div v-if="!readonly && !minimal" class="certificate-card__actions">
			<button
				class="certificate-card__action-btn certificate-card__action-btn--preview"
				type="button"
				@click="$emit('preview', certificate)"
			>
				👁️ Предпросмотр
			</button>
			<button
				class="certificate-card__action-btn"
				type="button"
				@click="$emit('edit', certificate)"
			>
				✏️
			</button>
			<button
				class="certificate-card__action-btn certificate-card__action-btn--delete"
				type="button"
				@click="handleDelete"
			>
				🗑️
			</button>
		</div>
	</div>
</template>

<script>
export default {
	name: 'CertificateCard',
	props: {
		certificate: {
			type: Object,
			required: true,
		},
		readonly: {
			type: Boolean,
			default: false,
		},
		minimal: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		parsedCriteria() {
			if (!this.certificate.criteria) return []
			try {
				const criteria =
					typeof this.certificate.criteria === 'string'
						? JSON.parse(this.certificate.criteria)
						: this.certificate.criteria
				return Array.isArray(criteria) ? criteria : []
			} catch (e) {
				return []
			}
		},
		previewImage() {
			return this.certificate.preview_image || null
		},
		templateConfig() {
			if (!this.certificate.template_data) return {}
			try {
				const data = typeof this.certificate.template_data === 'string'
					? JSON.parse(this.certificate.template_data)
					: this.certificate.template_data
				
				// template_data может быть в формате { content: {...}, criteria: [...] }
				// или напрямую { layers: [...], backgroundColor: ... }
				if (data.content) {
					return typeof data.content === 'string'
						? JSON.parse(data.content)
						: data.content
				}
				return data
			} catch (e) {
				return {}
			}
		},
		miniPreviewStyle() {
			const config = this.templateConfig
			let background = config.backgroundColor || '#fffef0'
			
			if (config.backgroundGradient) {
				background = `linear-gradient(135deg, ${config.backgroundGradient.start}, ${config.backgroundGradient.end})`
			}
			
			return { background }
		},
		borderStyle() {
			const border = this.templateConfig.borderStyle
			if (!border?.enabled) return {}
			
			return {
				borderColor: border.color || '#d4af37',
				borderWidth: `${Math.max(2, border.width / 4)}px`,
				borderStyle: border.style === 'double' ? 'double' : border.style || 'solid'
			}
		}
	},
	methods: {
		handleDelete() {
			if (
				confirm(
					`Вы уверены, что хотите удалить шаблон "${this.certificate.template_name}"? Это действие нельзя отменить.`
				)
			) {
				this.$emit('delete', this.certificate)
			}
		},
	},
}
</script>

<style scoped>
.certificate-card {
	background-color: #ffffff;
	border: 2px solid #e8e8e8;
	border-radius: 12px;
	overflow: hidden;
	transition: all 0.3s ease;
	display: flex;
	flex-direction: column;
  max-width: 320px;
}

.certificate-card:hover {
	border-color: #0095ec;
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.15);
}

/* Минимальный режим — только превью */
.certificate-card--minimal {
	border: none;
	background: transparent;
	max-width: 100%;
}

.certificate-card--minimal:hover {
	border-color: transparent;
	box-shadow: none;
}

.certificate-card--minimal .certificate-card__preview {
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
}

.certificate-card__preview {
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	padding: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 180px;
}

.certificate-card__preview--image {
	padding: 0;
	background: #f3f4f6;
}

.certificate-card__preview-image {
	width: 100%;
	height: 100%;
	object-fit: cover;
	display: block;
}

/* Мини-превью сертификата */
.certificate-card__mini-preview {
	width: 100%;
	height: 180px;
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 12px;
}

.mini-preview__border {
	position: absolute;
	inset: 8px;
	border: 3px double #d4af37;
	border-radius: 4px;
	pointer-events: none;
}

.mini-preview__content {
	text-align: center;
	z-index: 1;
}

.mini-preview__title {
	font-family: 'Georgia', serif;
	font-size: 16px;
	font-weight: bold;
	color: #1e3a8a;
	letter-spacing: 2px;
	margin-bottom: 2px;
}

.mini-preview__subtitle {
	font-family: 'Georgia', serif;
	font-size: 8px;
	font-style: italic;
	color: #666666;
	margin-bottom: 8px;
}

.mini-preview__variable {
	font-family: 'Times New Roman', serif;
	font-size: 12px;
	font-weight: bold;
	color: #1e3a8a;
	margin-bottom: 4px;
}

.mini-preview__text {
	font-family: Arial, sans-serif;
	font-size: 7px;
	color: #444444;
	margin-bottom: 2px;
}

.mini-preview__course {
	font-family: 'Georgia', serif;
	font-size: 10px;
	font-weight: bold;
	color: #1e3a8a;
	margin-bottom: 8px;
}

.mini-preview__date {
	font-family: Arial, sans-serif;
	font-size: 7px;
	color: #666666;
}

.certificate-card__content {
	padding: 20px;
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.certificate-card__title {
	color: #252641;
	font-family: 'Poppins', Helvetica;
	font-size: 18px;
	font-weight: 600;
	margin: 0;
}

.certificate-card__criteria {
	flex: 1;
}

.certificate-card__criteria-label {
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
	font-weight: 500;
	margin: 0 0 8px 0;
}

.certificate-card__criteria-list {
	margin: 0;
	padding-left: 20px;
}

.certificate-card__criteria-item {
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 12px;
	line-height: 1.6;
}

.certificate-card__meta {
	padding-top: 12px;
	border-top: 1px solid #e8e8e8;
}

.certificate-card__meta-item {
	color: #636363;
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
}

.certificate-card__actions {
	display: flex;
	gap: 8px;
	padding: 16px 20px;
	border-top: 2px solid #e8e8e8;
}

.certificate-card__action-btn {
	flex: 1;
	padding: 8px 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	background-color: #f5f5f5;
	border: none;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica;
	font-size: 13px;
	cursor: pointer;
	transition: all 0.2s ease;
}

.certificate-card__action-btn:hover {
	background-color: #e8e8e8;
	transform: translateY(-2px);
}

.certificate-card__action-btn--preview {
	flex: 2;
}

.certificate-card__action-btn--delete {
	flex: 0;
	width: 40px;
}

.certificate-card__action-btn--delete:hover {
	background-color: #f8d7da;
}
</style>

